import React, { useEffect, useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import moment from "moment";
import { useSnackbar } from "notistack";
import Paper from "@mui/material/Paper";
import GetAppIcon from "@mui/icons-material/GetApp";
import SchoolIcon from "@mui/icons-material/School";
import { withStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import LinearProgress from "@mui/material/LinearProgress";

import {
  Column,
  Mono,
  EditButton,
  StyledTableBody,
  auditLogsStyles,
  StyledTableContainer,
  StyledTableCell,
  NoMarginRow,
  SecondaryBar,
  StyledTableRow
} from "./styles";
import FilterBar from "./FilterBar";
import { Header } from "../../components";
import { copyText, truncate } from "../../util";
import DetailedTransaction from "./DetailedTransaction";
import { BodyContainer } from "../../components/BaseComponents";
import { NoResults } from "../../components/BaseComponents/NoResults";
import { AppSkeleton } from "../../components/BaseComponents/Skeleton";
import { getAnalyticsTransactions, getAnalyticsTransactionsForDownload } from "../../api/analytics";
import { Pagination, PaginationItem } from "@mui/material";

const elipsisItem = {
  ariaLabel: "Go to end-ellipsis page",
  onClick: null,
  page: null,
  selected: false,
  type: "end-ellipsis"
};

const rowsPerPage = 15;

const skeletonWidth = [160, 270, 280, 160, 50];

const getTotalPageCount = (rows) => Math.ceil(rows.length / rowsPerPage);

const getDefaultBeforeDate = () => {
  const today = new Date();
  return today - 3600 * 1000;
};

function AuditLogs(props) {
  const { classes, isHeaderHidden, organizationId } = props;

  // State variables
  const [lastQueryParams, setLastQueryParams] = useState(null);
  const [nextOffsetState, setNextoffSetState] = useState(-1);
  const [page, setPage] = useState(1);
  const [beforeDate, setBeforeDate] = useState(getDefaultBeforeDate());
  const [afterDate, setAfterDate] = useState(Date.now());
  const [queries, setQueries] = useState({
    projectIdQuery: "",
    userIdQuery: "",
    requestIds: []
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [rows, setRows] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const resultsContainerRef = useRef(null);

  // Function to fetch analytics data
  const getAnalytics = useCallback(
    ({ beforeDate, afterDate, userId, projectId, requestIds }) => {
      getAnalyticsTransactions({
        startDate: beforeDate,
        endDate: afterDate,
        userId: userId,
        projectId,
        offset: 0,
        organizationId,
        requestIds
      })
        .then(({ transactions, nextOffset }) => {
          // Reset expanded rows and scroll to top
          setExpandedRows(new Set());
          setLastQueryParams({ beforeDate, afterDate, userId, projectId, requestIds });
          resultsContainerRef.current?.scrollTo({ top: 0 });
          // Update state with new transactions
          setRows(transactions);
          setCount(getTotalPageCount(transactions));
          setPage(1);
          setNextoffSetState(nextOffset);
          setIsEmpty(transactions.length === 0);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setIsLoading(false);
          setIsEmpty(true);
          setRows([]);
          setNextoffSetState(-1);
          setPage(1);
          resultsContainerRef.current?.scrollTo({ top: 0 });
          setLastQueryParams({ beforeDate, afterDate, userId, projectId, requestIds });
        });
    },
    [organizationId]
  );

  const setQueriesHandler = useCallback(
    (q) => {
      const { projectIdQuery, userIdQuery, beforeDate, afterDate } = q;
      if (userIdQuery.trim() !== "" && projectIdQuery.trim() === "") {
        enqueueSnackbar("To search by userId, you must have projectId filtered", {
          variant: "error"
        });
        return;
      }
      setBeforeDate(new Date(beforeDate).getTime());
      setAfterDate(new Date(afterDate).getTime());
      setQueries(q);
    },
    [enqueueSnackbar]
  );

  const toggleRow = useCallback(
    (id) => {
      const expandedCopy = new Set(expandedRows);
      if (expandedCopy.has(id)) {
        expandedCopy.delete(id);
      } else {
        expandedCopy.add(id);
      }
      setExpandedRows(expandedCopy);
    },
    [expandedRows]
  );

  const handleClipboardCopy = useCallback(
    (id) => {
      copyText(id);
      enqueueSnackbar("Copied to clipboard");
    },
    [enqueueSnackbar]
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    const res = await getAnalyticsTransactionsForDownload({
      startDate: beforeDate,
      endDate: afterDate,
      userId: queries.userIdQuery,
      projectId: queries.projectIdQuery,
      offset: 0,
      requestIds: queries.requestIds
    });

    // Ensure that all possible columns are included
    const allColumns = [
      "requestUuid",
      "transactionTime",
      "voceraDoctorUserId",
      "inputIntentText",
      "inputClientContext",
      "inputIntentContext",
      "intentResponse",
      "intentDetectionMs",
      "entireTransactionMs",
      "intentType",
      "iusfJson",
      // Add Slot Type columns based on the maximum number of slots
      ...Array.from(
        { length: Math.max(...res.map((row) => Object.keys(row).filter((key) => key.startsWith("Slot Type")).length)) },
        (_, i) => `Slot Type ${i + 1}`
      )
    ];

    const csv = Papa.unparse(res, { columns: allColumns });
    const hiddenElement = document.createElement("a");
    hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
    hiddenElement.target = "_blank";
    hiddenElement.download = "s3_transactions.csv";
    hiddenElement.click();
    setIsExporting(false);
    enqueueSnackbar(`Exported ${res.length} transactions`);
  }, [beforeDate, afterDate, queries, enqueueSnackbar]);

  const onPageChange = useCallback(
    (event, page) => {
      if (!isPageLoading && page <= getTotalPageCount(rows)) {
        setPage(page);
      }
    },
    [isPageLoading, rows]
  );

  const showLastItem = useCallback(() => {
    const totalCount = getTotalPageCount(rows);
    return page > totalCount - 4;
  }, [page, rows]);

  const renderPaginationItem = useCallback(
    (item) => {
      const totalCount = getTotalPageCount(rows);
      if (totalCount < 2) return null;
      if (showLastItem()) {
        if (item.page === totalCount + 1 && item.type !== "next" && nextOffsetState !== -1) {
          return <PaginationItem {...elipsisItem} />;
        } else if (item.page === totalCount) {
          return <PaginationItem {...item} />;
        } else if (item.page === totalCount + 1 && item.type !== "next") {
          return null;
        }
        return <PaginationItem {...item} />;
      } else if (rows.length > 10 && (item.page === totalCount || item.page === totalCount + 1)) {
        return null;
      }

      return <PaginationItem {...item} />;
    },
    [showLastItem, rows, nextOffsetState]
  );

  useEffect(() => {
    if (isLoading) return;

    const currentParams = {
      beforeDate,
      afterDate,
      userId: queries.userIdQuery.trim(),
      projectId: queries.projectIdQuery.trim(),
      requestIds: queries.requestIds
    };

    if (lastQueryParams && JSON.stringify(currentParams) === JSON.stringify(lastQueryParams)) {
      return;
    }

    setIsLoading(true);

    getAnalytics(currentParams);
  }, [beforeDate, afterDate, rows, queries, isLoading, getAnalytics, lastQueryParams]);

  useEffect(() => {
    if (nextOffsetState === -1) return;

    if (page === getTotalPageCount(rows) && !isLoading) {
      setIsPageLoading(true);
      setIsLoading(true);
      getAnalyticsTransactions({
        startDate: beforeDate,
        endDate: afterDate,
        userId: queries.userIdQuery,
        projectId: queries.projectIdQuery,
        offset: nextOffsetState,
        organizationId,
        requestIds: queries.requestIds
      }).then(({ transactions, nextOffset }) => {
        const currRows = [...rows, ...transactions];
        setRows(currRows);
        setIsPageLoading(false);
        setIsLoading(false);
        setCount(getTotalPageCount(currRows));
        setNextoffSetState(nextOffset);
      });
    }
  }, [page, nextOffsetState, rows, isLoading, beforeDate, afterDate, queries, organizationId]);

  return (
    <>
      {!isHeaderHidden && <Header headerTitle={`Audit Logs`} onDrawerToggle={props.onDrawerToggle} />}
      <BodyContainer>
        <FilterBar startDate={beforeDate} endDate={afterDate} setQueries={setQueriesHandler} />
        <SecondaryBar>
          <div className="left"></div>
          <div className="right">
            <button onClick={handleExport} disabled={isExporting}>
              {!isExporting && (
                <>
                  <GetAppIcon /> Export
                </>
              )}
              {isExporting && (
                <>
                  <div className="loader">
                    <CircularProgress size={15} thickness={5} />
                  </div>
                  Downloading...
                </>
              )}
            </button>
          </div>
        </SecondaryBar>

        <Paper
          className={classes.paper}
          style={{
            maxHeight: `calc(100vh - ${getTotalPageCount(rows) < 2 ? 334 : 360}px)`
          }}
        >
          <div className={classes.contentWrapper}>
            <StyledTableContainer>
              <Table stickyHeader className={classes.table} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left" width="200">
                      Request
                    </StyledTableCell>
                    <StyledTableCell align="left" width="300">
                      Doctor
                    </StyledTableCell>
                    <StyledTableCell align="left">Intent Input Text</StyledTableCell>
                    <StyledTableCell align="center" width="100">
                      Duration
                    </StyledTableCell>
                    <StyledTableCell align="right">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <StyledTableBody ref={resultsContainerRef}>
                  {!isLoading &&
                    !isEmpty &&
                    rows.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage).map((row) => (
                      <React.Fragment key={row.requestUuid}>
                        <StyledTableRow>
                          <StyledTableCell component="th" scope="row">
                            <Column>
                              <div className="secondary" onClick={() => handleClipboardCopy(row.requestUuid)}>
                                <Mono>id</Mono> {truncate(row.requestUuid, 6)}...
                                {row?.internal && (
                                  <span className="internal-flag">
                                    <SchoolIcon />
                                  </span>
                                )}
                              </div>
                              <div className="primary">
                                {moment(row.transactionTime).format("MMM DD, YYYY h:mm:ss a")}
                              </div>
                            </Column>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <div className="primary">
                              <Mono>user</Mono> {row.voceraDoctorUserId}
                            </div>
                            <div className="secondary">
                              <Mono>project</Mono> {row.voceraProjectId}
                            </div>
                          </StyledTableCell>
                          <StyledTableCell align="left">{row.inputIntentText}</StyledTableCell>
                          <StyledTableCell align="center">
                            {row.audioDurationMs}
                            {row?.audioDurationMs && "ms"}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <EditButton onClick={() => toggleRow(row.requestUuid)}>
                              {expandedRows.has(row.requestUuid) ? "Close" : "View"}
                            </EditButton>
                          </StyledTableCell>
                        </StyledTableRow>
                        <NoMarginRow>
                          <TableCell style={{ padding: 0 }} colSpan={6}>
                            <DetailedTransaction row={row} expandedRows={expandedRows} />
                          </TableCell>
                        </NoMarginRow>
                      </React.Fragment>
                    ))}
                  {isLoading && <AppSkeleton widths={skeletonWidth} />}
                </StyledTableBody>
              </Table>
              {!isLoading && isEmpty && (
                <NoResults>
                  <p>No results match your search.</p>
                </NoResults>
              )}
            </StyledTableContainer>
          </div>
        </Paper>
        {isPageLoading && <LinearProgress style={{ width: "100%" }} />}
        {!isLoading && !isEmpty && (
          <Pagination
            count={count + 1}
            siblingCount={2}
            boundaryCount={1}
            onChange={onPageChange}
            page={page}
            renderItem={renderPaginationItem}
            style={{ marginBottom: "4px" }}
          />
        )}
      </BodyContainer>
    </>
  );
}

export default withStyles(auditLogsStyles)(AuditLogs);
